









































































import {Component, Mixins} from 'vue-mixin-decorator';
import {Watch} from 'vue-property-decorator';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Status} from './model/Messaging';

import {IApiService} from '@/services/IApi.service';
import {IAuthService} from '@/services/IAuth.service';
import {ISettingsService} from '@/services/settings/ISettings.service';
import {Setting, Theme} from '@/services/settings/Settings.types';

import {AppStore} from '@/store/App.store';
import {ApiStore} from '@/store/Api.store';
import {AuthStore} from '@/store/Auth.store';
import {MessagingStore} from '@/store/Messaging.store';

import Navigation from '@/components/Navigation.vue';
import Toolbar from '@/components/Toolbar.vue';
import Banners from '@/components/Banners/Banners.vue';

import {ConnectionCloseInfo} from '@verkehrsministerium/kraftfahrstrasse';
import GatewayUnreachableOverlay from '@/components/Utils/GatewayUnreachableOverlay.vue';
import SetupWizard from '@/components/Utils/SetupWizard.vue';
import {EventBus} from '@/utils/EventBus';
import {Notify} from '@/utils/Notify';

import {BaseComponent} from '@/mixins/BaseComponent';
import RedirectConfirmDialog from '@/components/Networking/RedirectConfirmDialog.vue';
import Notifications from '@/components/Notifications/Notifications.vue';
import NotificationsCenter from '@/components/Notifications/NotificationsCenter.vue';

@Component({
  components: {
    NotificationsCenter,
    Notifications,
    RedirectConfirmDialog,
    SetupWizard,
    GatewayUnreachableOverlay,
    Banners,
    Navigation,
    Toolbar,
  },
})
export default class App extends Mixins<BaseComponent>(BaseComponent) {
  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  @Inject(Symbol.for('MessagingStore'))
  private messagingStore!: MessagingStore;

  @Inject(Symbol.for('IAuthService'))
  private authService!: IAuthService;

  @Inject(Symbol.for('ISettingsService'))
  private settingsService!: ISettingsService;

  @Inject(Symbol.for('AuthStore'))
  private authStore!: AuthStore;

  private apiStore: ApiStore = getModule(ApiStore, this.$store);

  private appStore: AppStore = getModule(AppStore, this.$store);

  private messagingFirstConnection = true;

  private breadcrumbs: any[] = [];

  private drawerExpanded: boolean = true;

  private setupWizardOpen: boolean = false;

  private Status = Status;

  private AuthRefreshInProgress = false;

  private apiServiceConfigured = false;

  private version = process.env.VERSION;

  private async created() {
    this.logger.trace('created');

    this.onRouteChanged();

    this.apiService.notAvailableErrorHandler = this.notAvailableErrorHandler.bind(this);
    this.apiService.unauthorizedErrorHandler = this.unauthorizedErrorHandler.bind(this);

    await this.apiStore.loadRemote();
    await this.apiStore.loadSecured();
    if (!this.apiStore.Remote) {
      this.logger.fatal(`Undefined remote`);
      return;
    }

    this.apiService.configure(this.apiStore.Remote, this.apiStore.Secured);
    this.apiServiceConfigured = true;

    if (this.apiStore.ConfigurableRemote) {
      this.settingsService.set(Setting.RemoteAddress, this.apiStore.Remote);
      this.settingsService.set(Setting.Secured, this.apiStore.Secured);
    }

    this.logger.info(`Remote is ${this.apiStore.Remote}`);

    this.authStore.loadToken();
    if (!this.authStore.Token && this.$route.name !== 'login') {
      await this.$router.push({
        name: 'login',
        params: {nextUrl: this.$router.currentRoute.fullPath},
      });
    }

    this.$vuetify.theme.dark = this.settingsService.get(Setting.Theme) === Theme.Dark;

    this.messagingStore.DisconnectedHandlers.push(this.onMessagingDisconnected);

    if (this.$route.name !== 'login') {
      await this.connectMessaging();
    }

    EventBus.Register(EventBus.Event.SetupWizard, (open: boolean) => this.setupWizardOpen = open);
  }

  private async notAvailableErrorHandler(error: Error): Promise<void> {
    if (!this.apiStore.CheckSuspended) {
      this.checkApiAvailable();
    }
  }

  private async checkApiAvailable() {
    const available = await this.apiStore.checkAvailable(this.apiService.Timeout);
    this.appStore.setApiAvailable(available);
    if (available) {
      this.$forceUpdate();
    }
  }

  private async unauthorizedErrorHandler(error: Error): Promise<void> {
    this.authStore.logout();

    if (this.$route.name === 'login') {
      throw error;
    }

    Notify.Info(this.logger, 'Your session has expired, please log in');
    await this.$router.push({
      name: 'login',
      params: {nextUrl: this.$router.currentRoute.fullPath},
    });
  }

  @Watch('$route.path')
  private onRouteChanged() {
    this.updateTitle();
    this.updateBreadcrumb();
  }

  private setMessagingStatus(status: Status) {

    if (this.appStore.MessagingStatus === status) {
      return;
    }

    this.appStore.setMessagingStatus(status);

    switch (status) {
      case Status.Connected:
        if (this.messagingFirstConnection) {
          this.messagingFirstConnection = false;
        } else {
          Notify.Info(this.logger, 'You are connected to the gateway');
        }

        if (!this.appStore.ApiAvailable) {
          this.checkApiAvailable();
        }
        break;

      case Status.Disconnected:
        Notify.Warn(this.logger, `You are disconnected from the gateway`);
        break;
    }
  }

  private updateBreadcrumb() {
    this.breadcrumbs = [];
    for (const match of this.$route.matched) {
      this.breadcrumbs.push({
        text: match.meta.label,
        disabled: true,
      });
    }
  }

  private async connectMessaging() {
    if (!this.authStore.Token) {
      throw new Error('cannot connect messaging, no token provided');
    }

    if (!this.apiStore.Remote) {
      throw new Error('cannot connect messaging, no remote defined');
    }

    if (this.appStore.MessagingStatus === Status.Connected || this.appStore.MessagingStatus === Status.Connecting) {
      return;
    }

    try {
      this.logger.debug(`Connecting messaging...`);
      await this.messagingStore.connect(this.apiStore.Remote, this.apiStore.Secured, this.authStore.Token);
      this.logger.info(`Messaging is now connected`);
    } catch (error) {
      this.logger.error(`Cannot connect messaging, retrying in 5 seconds`);

      setTimeout(async () => {
        await this.connectMessaging();
      }, this.apiService.Timeout);
    } finally {
      this.setMessagingStatus(this.messagingStore.Status);
    }
  }

  private async onMessagingDisconnected(closeInfo: ConnectionCloseInfo): Promise<void> {
    this.logger.info(`Messaging disconnected: '${closeInfo.reason}'`, closeInfo);
    this.setMessagingStatus(Status.Disconnected);
    await this.connectMessaging();
  }

  private async disconnectMessaging() {
    try {
      await this.messagingStore.disconnect();
    } catch (error) {
      Notify.Error(this.logger, 'Could not disconnect messaging service', error);
    }
  }

  private updateTitle() {
    const title = process.env.VUE_APP_TITLE;

    if (title) {
      document.title = title;
    }

    for (const match of this.$route.matched) {
      if (match.meta.label != null) {
        document.title = `${match.meta.label} - ${document.title}`;
      }
    }
  }
}

